"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Modale de confirmation du site, à la place de window.confirm / window.prompt.
 * Un seul hôte (<ConfirmHost />, monté dans le layout racine) ; on l'appelle
 * de n'importe où : `if (!(await confirmer("Supprimer ?"))) return;`
 */
type Options = {
  /** Libellé du bouton de validation (par défaut « Confirmer »). */
  ok?: string;
  /** Bouton de validation rouge, pour les suppressions. */
  danger?: boolean;
};

type Demande =
  | (Options & { kind: "confirm"; message: string; resolve: (v: boolean) => void })
  | (Options & { kind: "prompt"; message: string; type?: "text" | "password"; resolve: (v: string | null) => void });

let ouvrir: ((d: Demande) => void) | null = null;

export function confirmer(message: string, options: Options = {}): Promise<boolean> {
  if (!ouvrir) return Promise.resolve(false);
  const show = ouvrir;
  return new Promise((resolve) => show({ ...options, kind: "confirm", message, resolve }));
}

/** Demande une saisie ; null si l'utilisateur annule. */
export function demander(message: string, options: Options & { type?: "text" | "password" } = {}): Promise<string | null> {
  if (!ouvrir) return Promise.resolve(null);
  const show = ouvrir;
  return new Promise((resolve) => show({ ...options, kind: "prompt", message, resolve }));
}

export function ConfirmHost() {
  const [file, setFile] = useState<Demande[]>([]);
  const [valeur, setValeur] = useState("");
  const okRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const d = file[0];

  useEffect(() => {
    ouvrir = (nouvelle) => setFile((f) => [...f, nouvelle]);
    return () => {
      ouvrir = null;
    };
  }, []);

  const fermer = (ok: boolean) => {
    if (!d) return;
    if (d.kind === "confirm") d.resolve(ok);
    else d.resolve(ok ? valeur : null);
    setValeur("");
    setFile((f) => f.slice(1));
  };

  useEffect(() => {
    if (!d) return;
    const precedent = document.activeElement as HTMLElement | null;
    (d.kind === "prompt" ? inputRef.current : okRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        fermer(false);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      precedent?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);

  if (!d) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onMouseDown={() => fermer(false)}>
      <form
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-message"
        className="card animate-pop w-full max-w-sm p-5 shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          fermer(true);
        }}
      >
        <p id="confirm-message" className="text-sm leading-relaxed">
          {d.message}
        </p>
        {d.kind === "prompt" && (
          <input
            ref={inputRef}
            type={d.type ?? "text"}
            value={valeur}
            onChange={(e) => setValeur(e.target.value)}
            autoComplete={d.type === "password" ? "current-password" : "off"}
            className="field mt-3 w-full"
          />
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={() => fermer(false)}>
            Annuler
          </button>
          <button ref={okRef} type="submit" className={`btn ${d.danger ? "btn-danger" : "btn-primary"}`}>
            {d.ok ?? "Confirmer"}
          </button>
        </div>
      </form>
    </div>
  );
}
